import { type EventSourceMessage, EventSourceParserStream } from "eventsource-parser/stream";

export interface SSEStreamOptions {
	maxRetries?: number | undefined;
	signal?: AbortSignal | undefined;
}

type FetchFn = (signal: AbortSignal) => Promise<Response>;

const DEFAULT_MAX_RETRIES = 2;
const MAX_BACKOFF_MS = 8000;

function calculateBackoff(attempt: number): number {
	const base = Math.min(500 * 2 ** attempt, MAX_BACKOFF_MS);
	const jitter = base * 0.25 * Math.random();
	return base + jitter;
}

function isRetryableStatus(status: number): boolean {
	return status >= 500 || status === 408 || status === 409 || status === 429;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
	return new Promise((resolve, reject) => {
		if (signal.aborted) {
			reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
			return;
		}
		const timer = setTimeout(resolve, ms);
		signal.addEventListener(
			"abort",
			() => {
				clearTimeout(timer);
				reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
			},
			{ once: true },
		);
	});
}

export class SSEStream<T = EventSourceMessage> implements AsyncIterable<T> {
	private controller: AbortController;
	private fetchFn: FetchFn;
	private maxRetries: number;
	private iterator: AsyncIterableIterator<T> | null = null;

	constructor(fetchFn: FetchFn, options: SSEStreamOptions = {}) {
		this.controller = new AbortController();
		this.fetchFn = fetchFn;
		this.maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;

		if (options.signal) {
			options.signal.addEventListener("abort", () => this.abort(), { once: true });
		}
	}

	static fromResponse(response: Response): SSEStream<EventSourceMessage> {
		const stream = new SSEStream<EventSourceMessage>(() => Promise.resolve(response), { maxRetries: 0 });
		return stream;
	}

	abort(): void {
		this.controller.abort();
	}

	[Symbol.asyncIterator](): AsyncIterableIterator<T> {
		if (!this.iterator) {
			this.iterator = this.stream();
		}
		return this.iterator;
	}

	private async *stream(): AsyncIterableIterator<T> {
		let lastError: unknown;

		for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
			if (attempt > 0) {
				await sleep(calculateBackoff(attempt - 1), this.controller.signal);
			}

			try {
				const response = await this.fetchFn(this.controller.signal);

				if (!response.ok) {
					const body = await response.json().catch(() => ({ message: "Request failed" }));
					const error = new Error(body.message || `HTTP ${response.status}`);
					if (isRetryableStatus(response.status) && attempt < this.maxRetries) {
						lastError = error;
						continue;
					}
					throw error;
				}

				if (!response.body) {
					throw new Error("No response body");
				}

				const eventStream = response.body
					.pipeThrough(new TextDecoderStream())
					.pipeThrough(new EventSourceParserStream());

				for await (const event of eventStream) {
					yield event as T;
				}

				return;
			} catch (error) {
				if (this.controller.signal.aborted) {
					throw error;
				}
				lastError = error;
				if (attempt === this.maxRetries) {
					throw error;
				}
			}
		}

		throw lastError;
	}
}
